/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { IntlProvider } from 'react-intl';
import translations from 'translations';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { signinRequest, clearRedirectPathname } from 'redux-api/action/auth';
import selectProfile from 'redux-api/reselect/profile';
import selectTeams, { getRequiredSection } from 'redux-api/reselect/teams';
import Loader from 'theme/loader';
import Disabled from 'theme/disabled';
import NotAllowed from 'theme/not-allowed';
import Page from 'theme/layouts/page';

function AuthSwitch({ children }) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { pathname, isReady, query } = router;

  const {
    isGetWaiting: isWaitingProfile,
    payload: { guid },
  } = useSelector((state) => state.profile);
  const { authState, redirectPathname, payload } = useSelector((state) => state.auth);
  const { isEnabled, redirectPath, onboardComplete, isAdmin, isAdminDe, needsPayment } = useSelector(selectProfile);
  const { canAccessSection } = useSelector(selectTeams);
  const { languageCode } = useSelector((state) => state.config);


  useEffect(() => {
    // Don't redirect to onboarding if there's an OAuth redirect pending
    if (redirectPath && !redirectPathname) {
      try {
        const currentPath = router.pathname;
        const redirectBasePath = new URL(redirectPath, window.location.origin).pathname;


        if (currentPath !== redirectBasePath) {
          router.push(redirectPath);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Invalid redirectPath:", redirectPath, error);
      }
    }
  }, [redirectPath, redirectPathname, router, onboardComplete, isAdmin, isAdminDe, authState]);

  // Handle redirect after successful OAuth signin
  useEffect(() => {
    if (authState === 'SIGNEDIN' && redirectPathname && redirectPathname !== '/') {
      const currentPath = router.pathname;
      
      // Only redirect if we're not already on the target path
      if (currentPath !== redirectPathname) {
        // eslint-disable-next-line no-console
        console.log('🎯 Auth-switch redirecting after signin:', redirectPathname);
        router.push(redirectPathname);
      }
      
      // Clear the redirect pathname after redirect to prevent loops
      dispatch(clearRedirectPathname());
    }
  }, [authState, redirectPathname, router, dispatch]);

  // Check if user needs payment and redirect to onboarding
  useEffect(() => {
    if (authState === 'SIGNEDIN' && !isWaitingProfile && guid && needsPayment) {
      const allowedPages = [
        '/profile',
        '/services',
        '/enable-service',
        '/billing/profile',
        '/billing/payment-account',
        '/billing/summary-billing-profile',
        '/billing/summary-payment-account',
        '/billing/profiles',
        '/onboarding',
      ];
      
      const isOnAllowedPage = allowedPages.includes(pathname);
      
      if (!isOnAllowedPage) {
        router.push('/onboarding');
      }
    }
  }, [authState, pathname, needsPayment, isWaitingProfile, guid, router]);

  // Check onboarding status on navigation
  useEffect(() => {
    if (authState === 'SIGNEDIN' && !isWaitingProfile && guid) {
      // Check if user needs onboarding and is not on an allowed page
      const allowedPages = [
        '/profile',
        '/services',
        '/enable-service',
        '/billing/profile',
        '/billing/payment-account',
        '/billing/summary-billing-profile',
        '/billing/summary-payment-account',
        '/billing/profiles',
        '/onboarding',
      ];
      
      const needsOnboarding = !isAdmin && !isAdminDe && !onboardComplete;
      const isOnAllowedPage = allowedPages.includes(pathname);
      
      
      if (needsOnboarding && !isOnAllowedPage) {
        router.push('/onboarding');
      }
    }
  }, [authState, pathname, onboardComplete, isAdmin, isAdminDe, isWaitingProfile, guid, router]);


  const locale = useMemo(() => 
    ['de', 'it', 'pt'].includes(languageCode) ? languageCode : 'en'
  , [languageCode]);

  const content = useMemo(() => {
    // Show loader while checking authentication or loading profile
    if (authState === 'LOADING' || (isWaitingProfile && !guid)) {
      return <Loader authState={authState} />;
    }

    // If not signed in, redirect to login
    if (authState === 'SIGNEDOUT') {
      router.push('/login');
      return <Loader authState="LOADING" />;
    }

    // If signed in but not enabled, show disabled page
    if (!isEnabled) return <Disabled />;

    // Block sections the team member has no scope for (direct URL access)
    const requiredSection = getRequiredSection(pathname);
    if (requiredSection && !canAccessSection(requiredSection)) {
      return (
        <Page>
          <NotAllowed />
        </Page>
      );
    }

    // User is signed in and enabled, show the page
    return <Page>{children}</Page>;
  }, [isWaitingProfile, isEnabled, authState, children, router, guid, pathname, canAccessSection]);

  useEffect(() => {
    if (!isReady) return;

    const { accessToken, idToken, refreshToken, timestamp, ...restQuery } = query;

    // Check if this is OAuth callback
    const isOAuthCallback = pathname === '/auth/oauth/streetlib/callback';
    
    if (isOAuthCallback) {
      // OAuth callback will be handled by the callback page component
      // No need to dispatch signin here
      return;
    }

    // Check if we have tokens in query string (legacy customer care scenario)
    // This check should happen BEFORE checking if user is already signed in
    const hasAllTokens = accessToken && idToken && refreshToken;
    
    if (hasAllTokens) {
      // Legacy token-based authentication (customer care impersonation)
      // eslint-disable-next-line no-console
      console.log('🔑 Found all 3 tokens in URL, using legacy authentication', {
        hasAccessToken: !!accessToken,
        hasIdToken: !!idToken,
        hasRefreshToken: !!refreshToken,
        pathname
      });
      dispatch(signinRequest({ pathname, query: restQuery, accessToken, idToken, refreshToken }));
      return;
    }

    // Don't verify tokens if user is already signed in (but only if no URL tokens)
    if (authState === 'SIGNEDIN') {
      // eslint-disable-next-line no-console
      console.log('✅ User already signed in, skipping token verification', { 
        authState,
        pathname
      });
      return;
    }
    
    // If loading and we have persisted tokens, verify them immediately
    if (authState === 'LOADING') {
      const persistedTokens = payload?.signInUserSession;
      
      if (persistedTokens?.accessToken) {
        // eslint-disable-next-line no-console
        console.log('🔄 Found persisted tokens while loading, verifying immediately', {
          hasAccessToken: !!persistedTokens.accessToken,
          hasOAuthMetadata: !!persistedTokens.oauthMetadata,
          pathname
        });
        
        dispatch(signinRequest({ 
          pathname, 
          query: {}, 
          ...persistedTokens 
        }));
        return;
      }
      
      // If loading but no persisted tokens and not on OAuth callback, check storage
      if (!isOAuthCallback) {
        // Check if there are any tokens in localStorage/sessionStorage
        const hasTokensInStorage = () => {
          try {
            // Check localStorage for persisted tokens
            const persistedAuth = localStorage.getItem('persist:root');
            if (persistedAuth) {
              const parsed = JSON.parse(persistedAuth);
              const authData = JSON.parse(parsed.auth || '{}');
              const tokens = authData.payload?.signInUserSession;
              return !!(tokens?.accessToken || tokens?.refreshToken);
            }
            return false;
          } catch (error) {
            return false;
          }
        };

        const hasTokens = hasTokensInStorage();
        
        if (!hasTokens) {
          // No tokens in storage, redirect to login
          // eslint-disable-next-line no-console
          console.log('🚫 No tokens in storage, redirecting to login', {
            authState,
            pathname,
            isOAuthCallback
          });
          router.push('/login');
          return;
        }

        // eslint-disable-next-line no-console
        console.log('⏳ Loading but no persisted tokens, waiting for auth state update', {
          authState,
          pathname,
          isOAuthCallback,
          hasTokensInStorage: hasTokens
        });
        // Don't redirect immediately, let the auth state settle
        return;
      }
      
      return;
    }

    // Check if we have only accessToken (shouldn't happen in normal flow)
    if (accessToken) {
      // Only accessToken without other tokens - this shouldn't happen in normal flow
      // Redirect to OAuth login
      router.push('/login');
    } else {
      // No tokens in query string - check if we have persisted tokens
      const persistedTokens = payload?.signInUserSession;
      
      if (persistedTokens?.accessToken) {
        // We have persisted tokens, verify them
        // eslint-disable-next-line no-console
        console.log('🔄 Found persisted tokens, verifying...', {
          hasAccessToken: !!persistedTokens.accessToken,
          hasOAuthMetadata: !!persistedTokens.oauthMetadata,
          pathname,
          currentAuthState: authState
        });
        
        dispatch(signinRequest({ 
          pathname, 
          query: restQuery, 
          ...persistedTokens 
        }));
      } else if (authState !== 'LOADING') {
        // No tokens at all - redirect to OAuth login only if not already loading
        // eslint-disable-next-line no-console
        console.log('🚫 No tokens found, redirecting to login');
        router.push('/login');
      } else {
        // No tokens in payload but loading - check storage to be sure
        const hasTokensInStorage = () => {
          try {
            const persistedAuth = localStorage.getItem('persist:root');
            if (persistedAuth) {
              const parsed = JSON.parse(persistedAuth);
              const authData = JSON.parse(parsed.auth || '{}');
              const tokens = authData.payload?.signInUserSession;
              return !!(tokens?.accessToken || tokens?.refreshToken);
            }
            return false;
          } catch (error) {
            return false;
          }
        };

        const hasTokens = hasTokensInStorage();
        
        if (!hasTokens) {
          // eslint-disable-next-line no-console
          console.log('🚫 No tokens in storage either, redirecting to login', {
            authState,
            pathname
          });
          router.push('/login');
        } else {
          // eslint-disable-next-line no-console
          console.log('⏳ No tokens in payload but found in storage, waiting for auth state update', {
            authState,
            pathname,
            hasTokensInStorage: hasTokens
          });
        }
      }
    }
  }, [dispatch, isReady, pathname, query, router, payload, authState]);

  return (
    <IntlProvider messages={translations[locale]} locale={locale} defaultLocale="en">
      <div style={{ height: '100%' }}>
        <Helmet htmlAttributes={{ lang: locale }} />
        {content}
      </div>
    </IntlProvider>
  );
}

export default AuthSwitch;